/**
 * @param {Date|string|number|null|undefined} date
 * @returns {string}
 */
export function formatTimestamp(date) {
  return date ? new Date(date).toLocaleString() : "";
}

/**
 * @template T
 * @param {T[]|null|undefined} messages
 * @param {(m: T) => boolean} [filterFn]
 * @returns {T|null}
 */
export function getMostRecentMessage(messages, filterFn = () => true) {
  if (!messages?.length) return null;
  return messages.filter(filterFn).reduce((latest, msg) => {
    const time = new Date(msg.insert_date || msg.timestamp || msg.created_at || 0);
    return time > new Date(latest?.insert_date || latest?.timestamp || latest?.created_at || 0) ? msg : latest;
  }, null);
}

/**
 * @template T
 * @param {T[]|null|undefined} messages
 * @returns {T|null}
 */
export function getMostRecentContactMessage(messages) {
  return getMostRecentMessage(messages, (m) => m.role === "CONTACT");
}

/**
 * @param {unknown[]} [msgs]
 * @returns {Array<Record<string, unknown>>}
 */
export function processViewerMessages(msgs) {
  if (!msgs?.length) return [];
  return msgs.map((msg, i) => ({
    ...msg,
    id: msg.id || `msg-${i}`,
    type: msg.role !== "CONTACT" ? "agent" : "contact",
    content: msg.content || msg.message,
    timestampStr:
      formatTimestamp(msg.timestamp || msg.insert_date) || msg.timestampStr || msg.insert_date || "",
    role: msg.role,
    status: msg.status,
    channel: msg.channel,
    real_message: msg,
  }));
}

/**
 * @param {Record<string, unknown>|null|undefined} message
 * @returns {string}
 */
function readMessageChannel(message) {
  if (!message || typeof message !== "object") return "";
  return String(message.channel || message.real_message?.channel || "")
    .toLowerCase()
    .trim();
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function channelConfigHasValue(value) {
  if (value == null) return false;
  if (Array.isArray(value)) return value.some((entry) => String(entry ?? "").trim().length > 0);
  return String(value).trim().length > 0;
}

/**
 * @param {Record<string, unknown>|null|undefined} campaign
 * @returns {Record<string, unknown>}
 */
function parseCampaignChannels(campaign) {
  if (!campaign || typeof campaign !== "object") return {};
  let channels = campaign.channels;
  if (typeof channels === "string") {
    try {
      channels = JSON.parse(channels);
    } catch {
      channels = {};
    }
  }
  return channels && typeof channels === "object" ? channels : {};
}

/**
 * @param {Record<string, unknown>|null|undefined} campaign
 * @returns {Record<string, unknown>}
 */
function parseCampaignMetadata(campaign) {
  if (!campaign || typeof campaign !== "object") return {};
  let metadata = campaign.metadata;
  if (typeof metadata === "string") {
    try {
      metadata = JSON.parse(metadata);
    } catch {
      metadata = {};
    }
  }
  return metadata && typeof metadata === "object" ? metadata : {};
}

/**
 * Unambiguous campaign-level channel when thread metadata is missing.
 *
 * @param {Record<string, unknown>|null|undefined} campaign
 * @returns {"voice"|"phone"|"email"|"sms"|"whatsapp"|"webchat"|null}
 */
export function resolveCampaignChannelKey(campaign) {
  if (!campaign) return null;

  const channels = parseCampaignChannels(campaign);
  const hasVoice = channelConfigHasValue(channels.VOICE) || channelConfigHasValue(channels.voice);
  const hasEmail = channelConfigHasValue(channels.EMAIL) || channelConfigHasValue(channels.email);
  const hasSms = channelConfigHasValue(channels.SMS) || channelConfigHasValue(channels.sms);
  const hasWhatsapp = channelConfigHasValue(channels.WHATSAPP) || channelConfigHasValue(channels.whatsapp);
  const hasWebchat = channelConfigHasValue(channels.WEBCHAT) || channelConfigHasValue(channels.webchat);

  if (hasVoice && !hasEmail) return "voice";
  if (hasEmail && !hasVoice) return "email";
  if (hasSms && !hasVoice && !hasEmail) return "sms";
  if (hasWhatsapp && !hasVoice && !hasEmail) return "whatsapp";
  if (hasWebchat && !hasVoice && !hasEmail) return "webchat";

  const metadata = parseCampaignMetadata(campaign);
  const typeHint = String(
    metadata.type || metadata.conversation_type || campaign.conversation || ""
  ).toLowerCase();
  if (
    typeHint.includes("voice") ||
    typeHint.includes("transcript") ||
    typeHint.includes("receptionist") ||
    typeHint.includes("phone")
  ) {
    return "voice";
  }

  return null;
}

/**
 * @param {Record<string, unknown>|null|undefined} conversation
 * @returns {boolean}
 */
function threadHasVoiceChannel(conversation) {
  if (!conversation) return false;
  const messages = conversation.messages;
  const hasVoice =
    Array.isArray(messages) && messages.some((m) => readMessageChannel(m) === "voice");
  const ch = String(conversation.channel || "").toLowerCase();
  return hasVoice || ch === "voice";
}

/**
 * @param {Record<string, unknown>|null|undefined} thread
 * @returns {boolean}
 */
function threadHasEmailAddresses(thread) {
  const messages = Array.isArray(thread?.messages) ? thread.messages : [];
  return (
    messages.some(
      (m) =>
        String(m.sender || "").includes("@") ||
        String(m.receiver || "").includes("@") ||
        String(m.real_message?.sender || "").includes("@") ||
        String(m.real_message?.receiver || "").includes("@")
    ) ||
    String(thread?.email || "").includes("@") ||
    String(thread?.contact_email || "").includes("@")
  );
}

/**
 * Normalized conversation channel id for UI (list badges, contact header).
 *
 * @param {Record<string, unknown>|null|undefined} thread
 * @param {{ campaign?: Record<string, unknown>|null }} [options]
 * @returns {"voice"|"phone"|"email"|"sms"|"whatsapp"|"webchat"|"chat"}
 */
export function resolveThreadChannelKey(thread, options = {}) {
  if (!thread) return "chat";

  const threadChannel = String(thread.channel || "").toLowerCase().trim();
  const messages = Array.isArray(thread.messages) ? thread.messages : [];
  const messageChannels = messages.map(readMessageChannel).filter(Boolean);

  if (threadChannel === "voice" || messageChannels.includes("voice")) return "voice";
  if (threadChannel === "phone" || messageChannels.includes("phone")) return "phone";

  const explicit = messageChannels.find((ch) =>
    ["email", "sms", "whatsapp", "webchat", "voice", "phone"].includes(ch)
  );
  if (explicit) return explicit;
  if (threadChannel && ["email", "sms", "whatsapp", "webchat"].includes(threadChannel)) {
    return threadChannel;
  }

  const campaignKey = resolveCampaignChannelKey(options.campaign);
  if (campaignKey) return campaignKey;

  if (threadHasEmailAddresses(thread)) return "email";
  return "chat";
}

/** @param {ReturnType<typeof resolveThreadChannelKey>} channelKey */
export function getThreadChannelDisplayLabel(channelKey) {
  const labels = {
    voice: "Voice",
    phone: "Phone",
    email: "Email",
    sms: "SMS",
    whatsapp: "WhatsApp",
    webchat: "Web Chat",
    chat: "Chat",
  };
  return labels[channelKey] || "Chat";
}

/** @param {ReturnType<typeof resolveThreadChannelKey>} channelKey */
export function isVoiceChannelKey(channelKey) {
  return channelKey === "voice" || channelKey === "phone";
}

/**
 * @param {Record<string, unknown>|null|undefined} conversation
 * @param {{ campaign?: Record<string, unknown>|null }} [options]
 * @returns {number}
 */
export function getConversationTab(conversation, options = {}) {
  return isVoiceChannelKey(resolveThreadChannelKey(conversation, options)) ? 1 : 0;
}

/**
 * @param {Record<string, unknown>|null|undefined} thread
 * @returns {string}
 */
export function getThreadId(thread) {
  return thread?.id || thread?.contact_email || thread?.email || "unknown";
}
